import {
    defaultBaseQuery,
    transformErrorResponse,
    transformRequest,
    transformSuccessResponse,
    buildFormData
} from '@/services';
import { createApi } from '@reduxjs/toolkit/query/react';

const scholarshipApi = createApi({
    reducerPath: 'scholarshipApi',
    baseQuery: defaultBaseQuery,
    endpoints: (builder) => ({
        getScholarships: builder.query({
            query: (params) => ({
                url: `scholarship`,
                method: 'GET',
                params: params,
                transformRequest: transformRequest
            }),
            transformErrorResponse: transformErrorResponse,
            transformResponse: transformSuccessResponse
        }),
        getScholarshipById: builder.query({
            query: (id) => ({
                url: `scholarship/${id}`,
                method: 'GET',
                transformRequest: transformRequest
            }),
            transformErrorResponse: transformErrorResponse,
            transformResponse: transformSuccessResponse
        }),
        filterScholarships: builder.mutation({
            query: (filters) => ({
                url: `scholarship/student-filter`,
                method: 'POST',
                body: filters,
                transformRequest: transformRequest
            }),
            transformErrorResponse: transformErrorResponse,
            transformResponse: transformSuccessResponse
        }),
        applyScholarship: builder.mutation({
            query: ({ appliedScholarshipData, documents, type }) => {
                return {
                    url: `scholarship/apply`,
                    method: 'POST',
                    params: { type },
                    body: buildFormData({ appliedScholarshipData, documents }),
                };
            },
            transformErrorResponse: transformErrorResponse,
            transformResponse: transformSuccessResponse
        }),
        getScholarshipDropdowns: builder.query({
            query: ({ scholarshipId }) => ({
                url: `dropdowns?pageName=${scholarshipId}`,
                method: 'GET',
                transformRequest: transformRequest
            }),
            transformErrorResponse: transformErrorResponse,
            transformResponse: transformSuccessResponse
        }),
        checkScholarshipApplied: builder.query({
            query: ({ scholarshipId }) => ({
                url: `scholarship/check-applied?scholarshipId=${scholarshipId}`,
                method: 'GET',
                transformRequest: transformRequest
            }),
            transformErrorResponse: transformErrorResponse,
            transformResponse: transformSuccessResponse
        }),
        getScholarshipUniversityDropdowns: builder.query({
            query: ({ page = 0, size = 1000, search = '' } = {}) => ({
                url: `dropdowns/university`,
                method: 'GET',
                params: {
                    page,
                    size,
                    ...(search ? { search } : {}),
                },
                transformRequest: transformRequest
            }),
            transformErrorResponse: transformErrorResponse,
            transformResponse: transformSuccessResponse
        }),
        getScholarshipInstituteDropdowns: builder.query({
            query: ({ page = 0, size = 10, search = '' }) => ({
                url: `dropdowns/institutes`,
                method: 'GET',
                params: {
                    page,
                    size,
                    ...(search ? { search } : {}),
                },
                transformRequest: transformRequest
            }),
            transformErrorResponse: transformErrorResponse,
            transformResponse: transformSuccessResponse
        }),
        getStudentDetails: builder.query({
            query: () => ({
                url: "scholarship/student-details",
                method: 'GET',
                transformRequest: transformRequest
            }),
            transformErrorResponse: transformErrorResponse,
            transformResponse: transformSuccessResponse
        }),
        getBankDetailsByIfsc: builder.query({
            queryFn: async (ifsc) => {
                try {
                    const response = await fetch(`https://ifsc.razorpay.com/${ifsc}`);
                    if (!response.ok) {
                        return { error: { status: response.status, data: await response.text() } };
                    }
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: error.message } };
                }
            }
        }),
        getMenus: builder.query({
            query: () => ({
                url: `menus`,
                method: "GET",
                transformRequest: transformRequest
            }),
            transformErrorResponse: transformErrorResponse,
            transformResponse: transformSuccessResponse
        }),
        getFilterData: builder.query({
            query: () => ({
                url: "scholarship/student-filter",
                method: "GET",
                transformRequest: transformRequest
            }),
            transformErrorResponse: transformErrorResponse,
            transformResponse: transformSuccessResponse
        }),

        getIncomeCertificate: builder.query({
            query: ({ incomecertificateId }) => ({
                url: `income-certificate`,
                method: 'POST',
                body: {
                    ID_Number: incomecertificateId
                },
                transformRequest: transformRequest
            }),
            transformErrorResponse: transformErrorResponse,
            transformResponse: (response) => response
        })
    })
})

export const {
    useLazyGetScholarshipsQuery,
    useLazyGetScholarshipByIdQuery,
    useFilterScholarshipsMutation,
    useApplyScholarshipMutation,
    useLazyGetScholarshipDropdownsQuery,
    useLazyGetBankDetailsByIfscQuery,
    useLazyGetScholarshipUniversityDropdownsQuery,
    useLazyGetScholarshipInstituteDropdownsQuery,
    useLazyCheckScholarshipAppliedQuery,
    useGetStudentDetailsQuery,
    useLazyGetStudentDetailsQuery,
    useGetFilterDataQuery,
    useLazyGetFilterDataQuery,
    useGetMenusQuery,
    useLazyGetMenusQuery,
    useLazyGetIncomeCertificateQuery
} = scholarshipApi;

export default scholarshipApi;